import React, { useEffect } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Rating from '@mui/material/Rating';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import { useTenant } from '../contexts/TenantContext';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import './EventCard.css'; // Import CSS file for AddToCalendarButton centering

import { submitEventComment } from '../services/businessService';

const EventGlobalRating = ({ event, readOnly = false }) => {
    const { t, i18n } = useTranslation();
    const { selectedTenantId } = useTenant();

    const [rating, setRating] = React.useState(0);
    const [hover, setHover] = React.useState(-1);

    const labels = {
        1: t('eventCard.rating.useless'),
        2: t('eventCard.rating.poor'),
        3: t('eventCard.rating.ok'),
        4: t('eventCard.rating.good'),
        5: t('eventCard.rating.excellent'),
    };

    function getLabelText(value) {
        const label = labels[value];
        return `${value} ${t('eventCard.rating.banana', { count: value })}${label ? `, ${label}` : ''}`;
    }

    useEffect(() => {
        setRating(event.event_user_state?.feedback_value || 0);
    }, [event]);

    const handleRatingChange = (eventObj, newValue) => {
        // Here you would typically send the new rating to the backend
        console.log(`New rating for event ${eventObj.id}: ${newValue}`);
        setRating(newValue);
        submitEventComment(selectedTenantId, event.event_id, newValue, "");
        // For demonstration, we'll just log it to the console
    };

    return (
        <Card sx={{ marginTop: 1, paddingTop: 0, paddingBottom: 0 }}>
            <CardContent>
                <Typography gutterBottom sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    whiteSpace: 'normal',
                    fontSize: '0.8rem',
                    textAlign: 'center',
                    fontWeight: 'bold',
                }}>
                    {t('eventCard.eventGlobalRating', { rating: (event.global_user_state?.feedback_average?.toFixed(1) || 'N/A').replace('.', ',') })}
                </Typography>
                <Typography gutterBottom sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    whiteSpace: 'normal',
                    fontSize: '1.0rem',
                    textAlign: 'center',
                    fontWeight: 'bold',
                }}>
                    {(event.global_user_state?.feedback_average?.toFixed(1) || 'N/A').replace('.', ',')} 🍌
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0, fontSize: '0.8rem', textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                        {t('eventCard.yourRatingHover')}
                    </Typography>
                    <Tooltip 
                        title={labels[hover !== -1 ? hover : event.feedback?.value] ? `${t('eventCard.itWas')} ${labels[hover !== -1 ? hover : event.feedback?.value]}` : ''}
                        arrow
                        placement="top"
                    >
                        <span style={{ cursor: 'pointer', display: 'inline-flex' }}>
                        { (readOnly) ? (
                            <Rating
                                name={`global-feedback-${event.id}`}
                                value={rating}
                                readOnly
                                precision={1}
                                emptyIcon={<span style={{ fontSize: '1.1rem' }}>🍌</span>}
                            />
                        ) : (
                            <Rating
                                name={`global-feedback-${event.id}`}
                                value={rating}
                                precision={1}
                                emptyIcon={<span style={{ fontSize: '1.1rem' }}>🍌</span>}
                                icon={<span style={{ fontSize: '1.1rem' }}>🍌</span>}
                                onChange={(eventObj, newValue) => {
                                    if (typeof newValue === 'number' && newValue > 0) {
                                        handleRatingChange(event, newValue);
                                    }
                                }}
                                onChangeActive={(eventObj, newHover) => {
                                    setHover(newHover);
                                }}
                        />
                        )}
                        </span>
                    </Tooltip>
                </Box>
                <Typography variant="caption" gutterBottom sx={{ display: 'block' }}>
                    {t('eventCard.basedOnRatings', { count: event.global_user_state?.feedback_count || 0 })}
                </Typography>
            </CardContent>
        </Card>
    );
};

export default EventGlobalRating;